<script setup lang="ts">
const props = defineProps<{
  schemaKey?: string | null
}>()

const QUESTION_COUNT = 4
const WORD_COUNT = 6
const WRITING_LINE_COUNT = 4
const OBSERVATION_COLUMN_COUNT = 4
const OBSERVATION_CELL_COUNT = 16
const TASK_COLUMN_COUNT = 3
const TASK_CELL_COUNT = 12
const FLUENCY_LINE_WIDTHS = ['100%', '97%', '99%', '94%', '62%']

const isObservationHeaderCell = (cellIndex: number) => cellIndex < OBSERVATION_COLUMN_COUNT
const isTaskHeaderCell = (cellIndex: number) => cellIndex % TASK_COLUMN_COUNT === 0

const PREVIEW_GROUND = 'size-full bg-gray-50'
const STACKED_PREVIEW = `${PREVIEW_GROUND} flex flex-col`
const TABLE_CELL = 'border-b border-r border-gray-300'
const HEADER_CELL = `${TABLE_CELL} relative bg-gray-300`
const HEADER_BAR = 'absolute h-0.5 rounded-xxs bg-gray-400'
</script>

<template>
  <div
    v-if="props.schemaKey === 'question_answer_sheet'"
    data-testid="schema-preview-question_answer_sheet"
    :class="PREVIEW_GROUND"
    class="grid grid-cols-2 grid-rows-2 gap-1.5 px-3 py-2"
  >
    <span v-for="question in QUESTION_COUNT" :key="question" class="relative rounded-xxs border border-gray-300">
      <span class="absolute left-1 top-1 size-1.5 bg-gray-900" />
    </span>
  </div>

  <div
    v-else-if="props.schemaKey === 'word_list_assessment'"
    data-testid="schema-preview-word_list_assessment"
    :class="PREVIEW_GROUND"
    class="grid grid-cols-2 grid-rows-3 gap-1 px-3 py-2"
  >
    <span v-for="word in WORD_COUNT" :key="word" class="flex items-center justify-center rounded-xxs border border-gray-300">
      <span class="h-1 w-7/12 rounded-xxs bg-gray-600" />
    </span>
  </div>

  <div
    v-else-if="props.schemaKey === 'writing_composition_assessment'"
    data-testid="schema-preview-writing_composition_assessment"
    :class="STACKED_PREVIEW"
    class="px-3 py-2"
  >
    <span class="mb-2 h-1 w-1/2 flex-none rounded-xxs bg-gray-600" />
    <span class="flex flex-col gap-2">
      <span v-for="line in WRITING_LINE_COUNT" :key="line" class="block h-px bg-gray-400" />
    </span>
  </div>

  <div
    v-else-if="props.schemaKey === 'observation_tracking'"
    data-testid="schema-preview-observation_tracking"
    :class="STACKED_PREVIEW"
    class="p-2"
  >
    <div class="grid h-full grid-cols-4 grid-rows-[15px_repeat(3,1fr)] overflow-hidden rounded-xxs border border-gray-300 [&>*:nth-child(4n)]:border-r-0">
      <template v-for="(_, cellIndex) in OBSERVATION_CELL_COUNT" :key="cellIndex">
        <span v-if="isObservationHeaderCell(cellIndex)" :class="HEADER_CELL">
          <span :class="HEADER_BAR" class="inset-x-1 top-1.5" />
        </span>
        <span v-else :class="TABLE_CELL" />
      </template>
    </div>
  </div>

  <div
    v-else-if="props.schemaKey === 'task_analysis'"
    data-testid="schema-preview-task_analysis"
    :class="STACKED_PREVIEW"
    class="p-2"
  >
    <div class="grid h-full grid-cols-[42%_1fr_1fr] grid-rows-4 overflow-hidden rounded-xxs border border-gray-300 [&>*:nth-child(3n)]:border-r-0">
      <template v-for="(_, cellIndex) in TASK_CELL_COUNT" :key="cellIndex">
        <span v-if="isTaskHeaderCell(cellIndex)" :class="HEADER_CELL">
          <span :class="HEADER_BAR" class="left-1 right-1.5 top-1/2 -translate-y-1/2" />
        </span>
        <span v-else :class="TABLE_CELL" />
      </template>
    </div>
  </div>

  <div
    v-else-if="props.schemaKey === 'fluency_running_record'"
    data-testid="schema-preview-fluency_running_record"
    :class="STACKED_PREVIEW"
    class="px-3 py-2"
  >
    <span class="mx-auto mb-2 mt-px h-1 w-1/2 flex-none rounded-xxs bg-gray-600" />
    <span class="flex flex-col gap-1">
      <span
        v-for="width in FLUENCY_LINE_WIDTHS"
        :key="width"
        class="block h-0.5 rounded-xxs bg-gray-400"
        :style="{ width }"
      />
    </span>
  </div>

  <div
    v-else
    data-testid="schema-preview-fallback"
    class="flex size-full flex-col items-center justify-center gap-2 bg-white text-gray-600"
  >
    <svg
      class="block"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M13.5 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8.5L13.5 3Z" />
      <path d="M13.5 3v5.5H19" />
      <path d="M8.5 12.5h7M8.5 15.5h7M8.5 18.5h4.5" />
    </svg>
    <span class="text-xs uppercase tracking-wider">Assessment</span>
  </div>
</template>
